import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import {
  ModalManagerService,
  PresetConfirmActionEnum,
} from 'src/app/services/modal-manager.service';
import { NotificationToasterService } from 'src/app/services/notification-toaster.service';

interface FileDeleteSelectionCounts {
  fileCount: number;
  folderCount: number;
}

interface FileDeleteResultCounts {
  deletedItemCount: number;
  requestedItemCount: number;
}

@Injectable({
  providedIn: 'root',
})
export class FileManagementService {
  constructor(
    private readonly modalManagerService: ModalManagerService,
    private readonly notificationToasterService: NotificationToasterService,
    private readonly translateService: TranslateService,
  ) {}

  /**
   * Opens the delete confirmation modal for the supplied file and folder counts.
   * Cancelled confirmations are filtered out, so subscribers only receive a value on confirm.
   *
   * @param selectionCounts Non-overlapping counts of files and folders selected for deletion.
   * @returns An observable that emits `true` once when deletion is confirmed and completes without
   * emitting when it is cancelled.
   */
  showDeleteConfirmation({
    fileCount,
    folderCount,
  }: FileDeleteSelectionCounts): Observable<boolean> {
    const { title, description } = this.getDeleteConfirmationCopy({ fileCount, folderCount });

    return this.modalManagerService
      .showConfirmationModalWithPreset(title, description, PresetConfirmActionEnum.DELETE)
      .pipe(
        take(1),
        filter((confirmed) => confirmed),
      );
  }

  /**
   * Shows one notification summarizing a completed or partially completed deletion request.
   * A complete deletion uses the standard success notification; a partial deletion uses a warning.
   *
   * @param resultCounts Counts of deleted items and total items requested for deletion.
   * @returns Nothing. Displays one success or warning notification as a side effect.
   */
  showDeleteResult({ deletedItemCount, requestedItemCount }: FileDeleteResultCounts): void {
    if (deletedItemCount < requestedItemCount) {
      this.notificationToasterService.showDefaultWarningNotificationV2(
        this.translateService.instant('{{deletedCount}} of {{requestedCount}} items deleted', {
          deletedCount: deletedItemCount,
          requestedCount: requestedItemCount,
        }),
        '',
        this.notificationToasterService.generateNotificationCode(),
        undefined,
        undefined,
        undefined,
        true,
      );
      return;
    }

    const message =
      deletedItemCount === 1
        ? this.translateService.instant('Item deleted')
        : this.translateService.instant('{{count}} items deleted', {
            count: deletedItemCount,
          });

    this.notificationToasterService.showDefaultSuccessNotification(
      message,
      this.notificationToasterService.generateNotificationCode(),
      true,
    );
  }

  /**
   * Shows the standard file-management error notification immediately.
   *
   * @param message Optional server-provided error message; the standard fallback is used when absent.
   * @returns Nothing. Displays an error notification as a side effect.
   */
  showDeleteError(message?: string): void {
    this.notificationToasterService.showDefaultErrorNotificationV2WithBuilder(
      message || this.translateService.instant('Something is wrong, Try again!'),
      '',
      this.notificationToasterService.generateNotificationCode(),
      (builder) => builder.onActivateTick(true),
    );
  }

  /**
   * Warns that protected folders in a delete selection were not deleted.
   *
   * @param protectedFolderNames Names of the protected folders excluded from deletion.
   * @returns Nothing. Displays a warning notification listing the protected folders as a side effect.
   */
  showProtectedFoldersWarning(protectedFolderNames: string[]): void {
    this.notificationToasterService.showDefaultWarningNotificationV2(
      this.translateService.instant(
        'The following item(s) are protected and cannot be moved or deleted: ',
      ) + protectedFolderNames.join(', '),
      '',
      this.notificationToasterService.generateNotificationCode(),
    );
  }

  private getDeleteConfirmationCopy({ fileCount, folderCount }: FileDeleteSelectionCounts): {
    title: string;
    description: string;
  } {
    const itemCount = fileCount + folderCount;
    const isSingleItem = itemCount === 1;
    const includesFolders = folderCount > 0;

    if (isSingleItem && includesFolders) {
      return {
        title: this.translateService.instant('Delete folder?'),
        description: this.translateService.instant(
          'Are you sure you want to delete this folder? Any content within this folder won’t be removed from any Spaces using it.',
        ),
      };
    }

    if (isSingleItem) {
      return {
        title: this.translateService.instant('Delete file?'),
        description: this.translateService.instant(
          'Are you sure you want to delete this file? It won’t be removed from any Spaces using it.',
        ),
      };
    }

    if (includesFolders) {
      return {
        title: this.translateService.instant('Delete items?'),
        description: this.translateService.instant(
          'Are you sure you want to delete these items? These items, and any content within them, won’t be removed from any Spaces using them.',
        ),
      };
    }

    return {
      title: this.translateService.instant('Delete files?'),
      description: this.translateService.instant(
        'Are you sure you want to delete these files? They won’t be removed from any Spaces using them.',
      ),
    };
  }
}
